import React, {useState} from 'react';

import {DestinationType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

import {ListEventSubscriptions} from './__generated/ListEventSubscriptions';
import {DESTINATION_TYPE_TO_DISPLAY_TEXT, isEventBridgeSubscription} from './EventsConstants';
import * as EventSubscriptionActions from './EventSubscriptionActions';
import EventTypesSection from './EventTypesSection';

type EventSubscription = ListEventSubscriptions['eventSubscriptionsListing']['eventSubscriptions'][number];

interface Props {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  availableAwsRegions: Array<string>;
  existingSubscriptions: Array<EventSubscription>;
}

export default function CreateEventbridgeEventSubscriptionModal(props: Props): JSX.Element {
  const [awsAccountId, setAwsAccountId] = useState('');
  const [awsRegion, setAwsRegion] = useState('');
  const [eventBusName, setEventBusName] = useState('');
  const [eventTypes, setEventTypes] = useState([] as Array<string>);
  const destinationType = DestinationType.EVENTBRIDGE;

  async function handleSubmit(): Promise<void> {
    const eventSubscription = await EventSubscriptionActions.create({
      destinationType,
      awsAccountId,
      awsRegion,
      eventTypes,
      eventBusName,
    });
    notifications.success(`Successfully created EventBridge subscription *${eventSubscription.name}*`);
    props.onRequestSubmit();
  }

  function isDuplicateSubscription(): boolean {
    return props.existingSubscriptions.some((eventSubscription) => {
      if (isEventBridgeSubscription(eventSubscription)) {
        return eventSubscription.awsAccountId === awsAccountId && eventSubscription.awsRegion === awsRegion;
      }
      return false;
    });
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Create Subscription"
      buttons={[
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: isDuplicateSubscription(),
          dataTestAttrs: {component: 'EventSubscriptionCreateModal', element: 'createButton'},
        },
      ]}
    >
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label>Delivery Method</Label>
            {/* For now, we only support one delivery method */}
            <SelectDropdown
              disabled={true}
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={destinationType}>
                  <Option key={destinationType} value={destinationType}>
                    {DESTINATION_TYPE_TO_DISPLAY_TEXT[destinationType]}
                  </Option>
                </Menu>
              }
              displayName={DESTINATION_TYPE_TO_DISPLAY_TEXT[destinationType]}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label>AWS Region</Label>
            <SelectDropdown
              dataTestAttrs={{component: 'SelectDropdown', element: 'region'}}
              menu={
                <SearchableMenu
                  width={Menu.WIDTHS.FULL}
                  optionDatas={props.availableAwsRegions.map((region: string) => ({
                    value: region,
                    name: region,
                  }))}
                  onSelect={(region: string) => {
                    setAwsRegion(region);
                  }}
                />
              }
              displayName={awsRegion || 'Select region...'}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <TextField
              label="AWS Account ID"
              value={awsAccountId}
              onChange={(e: InputChangeEvent) => {
                setAwsAccountId(e.target.value);
              }}
              placeholder="000000000000"
              dataTestAttrs={{component: 'TextField', element: 'awsAccountId'}}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <TextField
              label="Event Bus Name"
              value={eventBusName}
              onChange={(e: InputChangeEvent) => {
                setEventBusName(e.target.value);
              }}
              dataTestAttrs={{component: 'TextField', element: 'appName'}}
              placeholder="event-bus-name"
            />
          </FormGroup>
        </Col>
      </Row>
      <EventTypesSection eventTypes={eventTypes} onChangeEventTypes={setEventTypes} />
      {isDuplicateSubscription() && (
        <Row>
          <div className="text-warning u-padding">
            You may only have one subscription for an account and region combination.
          </div>
        </Row>
      )}
    </Modal>
  );
}
